import * as React from "react";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { SearchableMultiSelect } from '@/shared/components/forms';

interface JobType {
  id: number;
  group: string;
  title: string;
}

interface FormElements {
  position_type_eq_any: string[];
  job_type_ids?: string[];
  temp_date_eq?: string;
  [key: string]: unknown;
}

interface JobFiltersProps {
  form_elements: FormElements;
  job_types: JobType[];
  location: string;
  miles: number;
  onChangeSort: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeLocation: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onChangeCheckbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDate: (props: { date: unknown; fieldName: keyof FormElements }) => void;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeJobTypes: (selectedValues: string[]) => void;
}

// Design tokens
const tokens = {
  colors: {
    primary: '#3498db',
    primaryDark: '#2980b9',
    text: '#1a365d',
    textMuted: '#64748b',
    border: '#e2e8f0',
    borderFocus: '#3498db',
    bg: '#ffffff',
    bgHover: '#f8fafc',
    bgSubtle: '#f1f5f9',
    success: '#059669',
  },
  radius: {
    sm: '6px',
    md: '10px',
    lg: '14px',
  },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  borderRadius: tokens.radius.md,
  border: `1px solid ${tokens.colors.border}`,
  fontSize: '0.9375rem',
  color: tokens.colors.text,
  backgroundColor: tokens.colors.bg,
  transition: 'all 0.2s ease',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: tokens.colors.text,
  marginBottom: '0.5rem',
  letterSpacing: '0.01em',
};

export const JobFilters: React.FC<JobFiltersProps> = ({
  form_elements,
  job_types,
  location,
  miles,
  onChangeSort,
  onChangeLocation,
  onChangeCheckbox,
  onChangeDate,
  onChange,
  onChangeJobTypes,
}) => {
  const temp_is_checked = form_elements.position_type_eq_any.includes("temp");

  return (
    <div style={{
      background: tokens.colors.bg,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.colors.border}`,
      boxShadow: tokens.shadow.md,
    }}>
      {/* Header */}
      <div style={{
        padding: '1.25rem 1.5rem',
        borderBottom: `1px solid ${tokens.colors.border}`,
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          fontWeight: 700,
          color: tokens.colors.text,
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={tokens.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          Refine Search
        </h3>
      </div>

      {/* Body */}
      <div style={{ padding: '1.5rem' }}>
        {/* Sort */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="sort" style={labelStyle}>Sort By</label>
          <select
            id="sort"
            name="sort"
            onChange={onChangeSort}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              paddingRight: '2.5rem',
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
            onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
          >
            <option value="newest">Newest First</option>
            <option value="highest_salary">Highest Salary</option>
            <option value="lowest_salary">Lowest Salary</option>
            <option value="newest_temp">Newest Temp Jobs</option>
            <option value="highest_salary_temp">Highest Salary Temp</option>
            <option value="lowest_salary_temp">Lowest Salary Temp</option>
          </select>
        </div>

        {/* Location Group */}
        <div style={{
          background: tokens.colors.bgSubtle,
          borderRadius: tokens.radius.md,
          padding: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.875rem',
            color: tokens.colors.textMuted,
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            Location
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                name="location"
                value={location}
                onChange={onChangeLocation}
                placeholder="Zip code"
                style={{
                  ...inputStyle,
                  backgroundColor: tokens.colors.bg,
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
                onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
              />
            </div>
            <div style={{ width: '110px' }}>
              <select
                name="miles"
                value={miles}
                onChange={onChangeLocation}
                style={{
                  ...inputStyle,
                  backgroundColor: tokens.colors.bg,
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  paddingRight: '1.75rem',
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
                onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
              >
                <option value={5}>5 mi</option>
                <option value={10}>10 mi</option>
                <option value={25}>25 mi</option>
                <option value={50}>50 mi</option>
                <option value={100}>100 mi</option>
                <option value={200}>200 mi</option>
              </select>
            </div>
          </div>
        </div>

        {/* Position Type */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Position Type</label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '0.5rem',
          }}>
            {[
              { value: 'fulltime', label: 'Full Time', color: '#3498db' },
              { value: 'parttime', label: 'Part Time', color: '#9b59b6' },
              { value: 'longtermtemp', label: 'Long Term Temp', color: '#f39c12' },
              { value: 'temp', label: 'Temp', color: '#e74c3c' },
            ].map((type) => {
              const isChecked = form_elements.position_type_eq_any.includes(type.value);
              return (
                <label
                  key={type.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: tokens.radius.sm,
                    border: `1px solid ${isChecked ? type.color : tokens.colors.border}`,
                    background: isChecked ? `${type.color}08` : tokens.colors.bg,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8125rem',
                    fontWeight: isChecked ? 600 : 500,
                    color: isChecked ? type.color : tokens.colors.textMuted,
                  }}
                  onMouseOver={(e) => {
                    if (!isChecked) {
                      e.currentTarget.style.borderColor = type.color;
                      e.currentTarget.style.background = `${type.color}05`;
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isChecked) {
                      e.currentTarget.style.borderColor = tokens.colors.border;
                      e.currentTarget.style.background = tokens.colors.bg;
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    name="position_type_eq_any"
                    value={type.value}
                    id={type.value}
                    checked={isChecked}
                    onChange={onChangeCheckbox}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '4px',
                    border: `2px solid ${isChecked ? type.color : tokens.colors.border}`,
                    background: isChecked ? type.color : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                  }}>
                    {isChecked && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </span>
                  {type.label}
                </label>
              );
            })}
          </div>
        </div>

        {/* Temp Date Picker */}
        {temp_is_checked && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Temp Date</label>
            <Datetime
              timeFormat={false}
              value={form_elements.temp_date_eq || ""}
              onChange={(date: unknown) => onChangeDate({ date, fieldName: "temp_date_eq" })}
              inputProps={{
                placeholder: "Select a date",
                style: inputStyle,
                onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.style.borderColor = tokens.colors.borderFocus,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.style.borderColor = tokens.colors.border,
              }}
            />
          </div>
        )}

        {/* Job Type */}
        <div>
          <label htmlFor="job_type_group_eq_any" style={labelStyle}>
            Job Type
          </label>
          <SearchableMultiSelect
            options={job_types.map(job_type => ({
              value: job_type.id.toString(),
              label: job_type.title
            }))}
            value={form_elements.job_type_ids || []}
            onChange={onChangeJobTypes}
            placeholder="All job types"
            isMulti={true}
            isClearable={true}
            name="job_type_group_eq_any"
          />
        </div>
      </div>

      {/* Footer hint */}
      <div style={{
        padding: '1rem 1.5rem',
        borderTop: `1px solid ${tokens.colors.border}`,
        background: tokens.colors.bgSubtle,
        fontSize: '0.75rem',
        color: tokens.colors.textMuted,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        Results update automatically
      </div>
    </div>
  );
};

export default JobFilters;
